<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useFontSize } from 'dashboard/composables/useFontSize';
import { LocalStorage } from 'shared/helpers/localStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { setColorTheme } from 'dashboard/helper/themeHelper';
import wootConstants from 'dashboard/constants/globals';
import voiceSipSessionAPI from 'dashboard/api/voiceSipSession';

import SidebarProfileMenu from 'next/sidebar/SidebarProfileMenu.vue';
import {
  DropdownBody,
  DropdownContainer,
  DropdownItem,
  DropdownSeparator,
  DropdownSection,
} from 'next/dropdown-menu/base';

const emit = defineEmits(['showCreateAccountModal', 'openKeyShortcutModal']);

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const { accountScopedRoute } = useAccount();
const { updateFontSize, currentFontSize } = useFontSize();

const currentUserAvailability = useMapGetter('getCurrentUserAvailability');
const currentAccountId = useMapGetter('getCurrentAccountId');
const currentRole = useMapGetter('getCurrentRole');
const notificationCount = useMapGetter('notifications/getUnreadCount');
const isAdministrator = computed(() => currentRole.value === 'administrator');

const sipRegistered = ref(false);
const sipConfigured = ref(false);
const sipExtension = ref('');

const { AVAILABILITY_STATUS_KEYS } = wootConstants;
const brandLogoSrc = '/brand-assets/logo_thumbnail.svg';

const navItems = computed(() =>
  [
    {
      key: 'inbox',
      label: t('SIDEBAR.HOME'),
      icon: 'i-lucide-house',
      to: accountScopedRoute('home'),
      activeOn: ['home'],
    },
    {
      key: 'conversations',
      label: t('SIDEBAR.CONVERSATIONS'),
      icon: 'i-lucide-message-circle',
      to: accountScopedRoute('conversations'),
      activeOn: [
        'conversations',
        'inbox_view',
        'inbox_view_conversation',
        'inbox_conversation',
        'inbox_dashboard',
        'conversation_through_inbox',
        'label_conversations',
        'conversations_through_label',
        'team_conversations',
        'conversations_through_team',
        'folder_conversations',
        'conversations_through_folders',
        'conversation_mentions',
        'conversation_through_mentions',
        'conversation_unattended',
        'conversation_through_unattended',
        'conversation_participating',
        'conversation_through_participating',
      ],
      count: notificationCount.value,
    },
    {
      key: 'captain',
      adminOnly: true,
      label: t('SIDEBAR.CAPTAIN'),
      icon: 'i-woot-captain',
      to: accountScopedRoute('captain_assistants_index', {
        navigationPath: 'captain_assistants_responses_index',
      }),
      activePrefix: 'captain',
    },
    {
      key: 'contacts',
      adminOnly: true,
      label: t('SIDEBAR.CONTACTS'),
      icon: 'i-lucide-contact',
      to: accountScopedRoute('contacts_dashboard_index', {}, { page: 1 }),
      activePrefix: 'contacts',
    },
    {
      key: 'reports',
      adminOnly: true,
      label: t('SIDEBAR.REPORTS'),
      icon: 'i-lucide-chart-spline',
      to: accountScopedRoute('account_overview_reports'),
      activeOn: [
        'account_overview_reports',
        'conversation_reports',
        'agent_reports_index',
        'agent_reports_show',
        'label_reports_index',
        'label_reports_show',
        'inbox_reports_index',
        'inbox_reports_show',
        'team_reports_index',
        'team_reports_show',
        'csat_reports',
        'campaign_reports',
        'sla_reports',
        'bot_reports',
      ],
    },
    {
      key: 'campaigns',
      adminOnly: true,
      label: t('SIDEBAR.CAMPAIGNS'),
      icon: 'i-lucide-megaphone',
      to: accountScopedRoute('campaigns_email_index'),
      activePrefix: 'campaigns',
    },
  ].filter(item => !item.adminOnly || isAdministrator.value)
);

const settingsItems = computed(() => {
  if (!isAdministrator.value) return [];

  return [
    {
      label: t('SIDEBAR.ACCOUNT_SETTINGS'),
      icon: 'i-lucide-briefcase',
      link: accountScopedRoute('general_settings_index'),
    },
    {
      label: t('SIDEBAR.AGENTS'),
      icon: 'i-lucide-square-user',
      link: accountScopedRoute('agent_list'),
    },
    {
      label: t('SIDEBAR.TEAMS'),
      icon: 'i-lucide-users',
      link: accountScopedRoute('settings_teams_list'),
    },
    {
      label: t('SIDEBAR.INBOXES'),
      icon: 'i-lucide-inbox',
      link: accountScopedRoute('settings_inbox_list'),
    },
    {
      label: t('SIDEBAR.AUTOMATION'),
      icon: 'i-lucide-workflow',
      link: accountScopedRoute('automation_list'),
    },
    {
      label: t('SIDEBAR.MACROS'),
      icon: 'i-lucide-toy-brick',
      link: accountScopedRoute('macros_wrapper'),
    },
    {
      label: t('SIDEBAR.INTEGRATIONS'),
      icon: 'i-lucide-blocks',
      link: accountScopedRoute('settings_applications'),
    },
    {
      label: t('SIDEBAR.SECURITY'),
      icon: 'i-lucide-shield',
      link: accountScopedRoute('security_settings_index'),
    },
  ];
});

const settingsRouteNames = computed(() => {
  return settingsItems.value.map(item => item.link?.name).filter(Boolean);
});

const statusOptions = computed(() => [
  {
    value: AVAILABILITY_STATUS_KEYS[0],
    label: t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.ONLINE'),
    className: 'tic-status-dot--online',
  },
  {
    value: AVAILABILITY_STATUS_KEYS[1],
    label: t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.BUSY'),
    className: 'tic-status-dot--busy',
  },
  {
    value: AVAILABILITY_STATUS_KEYS[2],
    label: t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.OFFLINE'),
    className: 'tic-status-dot--offline',
  },
]);

const activeStatus = computed(() => {
  return (
    statusOptions.value.find(
      status => status.value === currentUserAvailability.value
    ) || statusOptions.value[0]
  );
});

const themeSelection = ref(
  LocalStorage.get(LOCAL_STORAGE_KEYS.COLOR_SCHEME) || 'auto'
);
const selectedTheme = computed(() => themeSelection.value);

const fontSizePercent = computed(() => {
  const current = parseInt(currentFontSize.value || '16px', 10);
  return `${Math.round((current / 16) * 100)}%`;
});

const accessibilitySettings = ref({
  highContrast: LocalStorage.get('tic_a11y_high_contrast') === 'true',
  reduceMotion: LocalStorage.get('tic_a11y_reduce_motion') === 'true',
  readableFont: LocalStorage.get('tic_a11y_readable_font') === 'true',
  focusMode: LocalStorage.get('tic_a11y_focus_mode') === 'true',
});

const sipDotClass = computed(() => {
  if (sipRegistered.value) return 'tic-status-dot--online';
  if (sipConfigured.value) return 'tic-status-dot--busy';
  return 'tic-status-dot--offline';
});

const sipLabel = computed(() => {
  if (sipRegistered.value) return `SIP ${sipExtension.value}`;
  if (sipConfigured.value) return t('TIC_TOPBAR.SIP.CONNECTING');
  return t('TIC_TOPBAR.SIP.PENDING');
});

const isNavActive = item => {
  if (item.activeOn?.includes(route.name)) return true;
  if (item.activePrefix) {
    return String(route.name || '').startsWith(item.activePrefix);
  }
  return false;
};

const isSettingsActive = computed(() => {
  const routeName = String(route.name || '');
  return (
    routeName.includes('settings') ||
    settingsRouteNames.value.includes(route.name)
  );
});

const changeAvailabilityStatus = availability => {
  try {
    store.dispatch('updateAvailability', {
      availability,
      account_id: currentAccountId.value,
    });
  } catch {
    useAlert(t('PROFILE_SETTINGS.FORM.AVAILABILITY.SET_AVAILABILITY_ERROR'));
  }
};

const setTheme = theme => {
  themeSelection.value = theme;
  LocalStorage.set(LOCAL_STORAGE_KEYS.COLOR_SCHEME, theme);
  setColorTheme(
    window.matchMedia('(prefers-color-scheme: dark)').matches,
    theme
  );
};

const setZoom = size => {
  updateFontSize(size);
};

const applyAccessibilitySettings = () => {
  const root = document.documentElement;
  root.classList.toggle(
    'tic-a11y-high-contrast',
    accessibilitySettings.value.highContrast
  );
  root.classList.toggle(
    'tic-a11y-reduce-motion',
    accessibilitySettings.value.reduceMotion
  );
  root.classList.toggle(
    'tic-a11y-readable-font',
    accessibilitySettings.value.readableFont
  );
  root.classList.toggle(
    'tic-a11y-focus-mode',
    accessibilitySettings.value.focusMode
  );
};

const toggleAccessibility = key => {
  accessibilitySettings.value[key] = !accessibilitySettings.value[key];
  LocalStorage.set(
    `tic_a11y_${key.replace(/[A-Z]/g, match => `_${match.toLowerCase()}`)}`,
    accessibilitySettings.value[key]
  );
  applyAccessibilitySettings();
};

const openVoiceDialer = () => {
  window.dispatchEvent(new CustomEvent('voice:openDialer'));
};

const openSipConfig = () => {
  window.dispatchEvent(new CustomEvent('voice:openSipConfig'));
};

const hydrateSipConfig = async () => {
  try {
    const { data } = await voiceSipSessionAPI.getConfig();
    if (data?.configured && data.sip_config?.extension) {
      sipConfigured.value = true;
      sipExtension.value = data.sip_config.extension;
      return;
    }
  } catch {
    // Fallback to legacy local storage state.
  }

  try {
    const currentUser = store.getters.getCurrentUser;
    const accountId = store.getters.getCurrentAccountId;
    if (currentUser?.id && accountId) {
      const stored = JSON.parse(
        localStorage.getItem(`vc_sip_${accountId}_${currentUser.id}`) || 'null'
      );
      if (stored?.extension && stored?.password) {
        sipConfigured.value = true;
        sipExtension.value = stored.extension;
      }
    }
  } catch {
    // Ignore unreadable local storage state.
  }
};

const onSipRegistered = event => {
  sipRegistered.value = true;
  sipConfigured.value = true;
  sipExtension.value = event.detail?.userInfo?.extension || sipExtension.value;
};

const onSipUnregistered = () => {
  sipRegistered.value = false;
};

const onSipNotConfigured = () => {
  sipRegistered.value = false;
  sipConfigured.value = false;
};

onMounted(() => {
  store.dispatch('labels/get');
  store.dispatch('inboxes/get');
  store.dispatch('notifications/unReadCount');
  store.dispatch('teams/get');
  store.dispatch('attributes/get');
  store.dispatch('customViews/get', 'conversation');
  store.dispatch('customViews/get', 'contact');
  applyAccessibilitySettings();

  hydrateSipConfig();

  if (window.voiceSIPClient?.isRegistered) sipRegistered.value = true;
  window.addEventListener('sip:registered', onSipRegistered);
  window.addEventListener('sip:unregistered', onSipUnregistered);
  window.addEventListener('voice:sipNotConfigured', onSipNotConfigured);
});

onUnmounted(() => {
  window.removeEventListener('sip:registered', onSipRegistered);
  window.removeEventListener('sip:unregistered', onSipUnregistered);
  window.removeEventListener('voice:sipNotConfigured', onSipNotConfigured);
});
</script>

<!-- eslint-disable vue/no-bare-strings-in-template -->
<!-- eslint-disable @intlify/vue-i18n/no-raw-text -->
<template>
  <header class="tic-topbar">
    <div class="tic-topbar__brand">
      <div class="tic-topbar__mark" aria-hidden="true">
        <img :src="brandLogoSrc" alt="1980 Tic Cloud" />
      </div>
      <div class="tic-topbar__brand-text">
        <strong>19 80 Tic Cloud</strong>
        <span>{{ t('TIC_TOPBAR.BRAND_SUBTITLE') }}</span>
      </div>
    </div>

    <nav class="tic-topbar__nav" :aria-label="t('TIC_TOPBAR.MAIN_NAV')">
      <RouterLink
        v-for="item in navItems"
        :key="item.key"
        :to="item.to"
        class="tic-topbar__nav-item"
        :class="{ 'is-active': isNavActive(item) }"
        :title="item.label"
      >
        <span :class="item.icon" class="tic-topbar__nav-icon" />
        <span class="sr-only">{{ item.label }}</span>
        <span v-if="item.count" class="tic-topbar__badge">
          {{ item.count }}
        </span>
      </RouterLink>

      <RouterLink
        :to="accountScopedRoute('settings_home')"
        class="tic-topbar__nav-item"
        :class="{ 'is-active': isSettingsActive }"
        :title="t('SIDEBAR.SETTINGS')"
      >
        <span class="i-lucide-settings tic-topbar__nav-icon" />
        <span class="sr-only">{{ t('SIDEBAR.SETTINGS') }}</span>
      </RouterLink>
    </nav>

    <div class="tic-topbar__actions">
      <RouterLink
        :to="accountScopedRoute('search')"
        class="tic-topbar__icon-action tic-topbar__search"
        :title="t('TIC_TOPBAR.SEARCH_PLATFORM')"
      >
        <span class="i-lucide-search size-4" />
        <span class="sr-only">{{ t('TIC_TOPBAR.SEARCH') }}</span>
      </RouterLink>

      <button
        type="button"
        class="tic-topbar__sip"
        :title="t('TIC_TOPBAR.CALL')"
        @click="openVoiceDialer"
      >
        <span class="i-lucide-phone-call size-4" />
        <span class="tic-status-dot" :class="sipDotClass" />
        <span class="sr-only">{{ sipLabel }}</span>
      </button>

      <DropdownContainer>
        <template #trigger="{ toggle, isOpen }">
          <button
            class="tic-topbar__icon-action"
            :class="{ 'is-active': isOpen }"
            type="button"
            :title="t('TIC_TOPBAR.ACCESSIBILITY.TITLE')"
            @click="toggle"
          >
            <span class="i-lucide-user-round size-4" />
            <span class="sr-only">
              {{ t('TIC_TOPBAR.ACCESSIBILITY.TITLE') }}
            </span>
          </button>
        </template>
        <DropdownBody
          class="tic-topbar__dropdown ltr:right-0 rtl:left-0 top-0 mt-0 w-80"
        >
          <DropdownSection :title="t('TIC_TOPBAR.ACCESSIBILITY.VISUAL_THEME')">
            <div class="tic-topbar__segmented">
              <button
                type="button"
                :class="{ 'is-active': selectedTheme === 'dark' }"
                @click="setTheme('dark')"
              >
                {{ t('TIC_TOPBAR.ACCESSIBILITY.DARK') }}
              </button>
              <button
                type="button"
                :class="{ 'is-active': selectedTheme === 'light' }"
                @click="setTheme('light')"
              >
                {{ t('TIC_TOPBAR.ACCESSIBILITY.LIGHT') }}
              </button>
              <button
                type="button"
                :class="{ 'is-active': selectedTheme === 'auto' }"
                @click="setTheme('auto')"
              >
                {{ t('TIC_TOPBAR.ACCESSIBILITY.AUTO') }}
              </button>
            </div>
          </DropdownSection>
          <DropdownSeparator />
          <DropdownSection :title="`Zoom ${fontSizePercent}`">
            <div class="tic-topbar__segmented">
              <button type="button" @click="setZoom('14px')">A-</button>
              <button type="button" @click="setZoom('16px')">100%</button>
              <button type="button" @click="setZoom('18px')">A+</button>
            </div>
          </DropdownSection>
          <DropdownSeparator />
          <DropdownSection :title="t('TIC_TOPBAR.ACCESSIBILITY.TITLE')">
            <div class="tic-a11y-panel">
              <button
                type="button"
                :class="{ 'is-active': accessibilitySettings.highContrast }"
                @click="toggleAccessibility('highContrast')"
              >
                <span class="i-lucide-contrast" />
                {{ t('TIC_TOPBAR.ACCESSIBILITY.HIGH_CONTRAST') }}
              </button>
              <button
                type="button"
                :class="{ 'is-active': accessibilitySettings.reduceMotion }"
                @click="toggleAccessibility('reduceMotion')"
              >
                <span class="i-lucide-circle-dashed" />
                {{ t('TIC_TOPBAR.ACCESSIBILITY.REDUCE_MOTION') }}
              </button>
              <button
                type="button"
                :class="{ 'is-active': accessibilitySettings.readableFont }"
                @click="toggleAccessibility('readableFont')"
              >
                <span class="i-lucide-book-open-text" />
                {{ t('TIC_TOPBAR.ACCESSIBILITY.READABLE_FONT') }}
              </button>
              <button
                type="button"
                :class="{ 'is-active': accessibilitySettings.focusMode }"
                @click="toggleAccessibility('focusMode')"
              >
                <span class="i-lucide-focus" />
                {{ t('TIC_TOPBAR.ACCESSIBILITY.VISIBLE_FOCUS') }}
              </button>
            </div>
          </DropdownSection>
        </DropdownBody>
      </DropdownContainer>

      <DropdownContainer>
        <template #trigger="{ toggle, isOpen }">
          <button
            class="tic-topbar__status"
            :class="{ 'is-active': isOpen }"
            type="button"
            :title="t('TIC_TOPBAR.STATUS.CHANGE')"
            @click="toggle"
          >
            <span class="tic-status-dot" :class="activeStatus.className" />
            <span>{{ activeStatus.label }}</span>
            <span class="i-lucide-chevron-down size-3" />
          </button>
        </template>
        <DropdownBody
          class="tic-topbar__dropdown ltr:right-0 rtl:left-0 top-0 mt-0 w-44"
        >
          <DropdownItem
            v-for="status in statusOptions"
            :key="status.value"
            class="cursor-pointer"
            :label="status.label"
            :click="() => changeAvailabilityStatus(status.value)"
          >
            <span class="tic-status-dot" :class="status.className" />
            <span>{{ status.label }}</span>
          </DropdownItem>
        </DropdownBody>
      </DropdownContainer>

      <button
        type="button"
        class="tic-topbar__icon-action"
        :title="t('TIC_TOPBAR.SIP.CONFIGURE')"
        @click="openSipConfig"
      >
        <span class="i-lucide-settings-2 size-4" />
      </button>

      <div class="tic-topbar__profile">
        <SidebarProfileMenu
          compact
          body-class="tic-topbar__profile-dropdown top-0 z-50 mt-0 w-80 ltr:right-0 rtl:left-0"
          @open-key-shortcut-modal="emit('openKeyShortcutModal')"
          @show-create-account-modal="emit('showCreateAccountModal')"
        />
      </div>
    </div>
  </header>
</template>

<style scoped lang="scss">
.tic-topbar {
  --tic-glass: #071827;
  --tic-border: rgba(148, 234, 255, 0.18);

  position: relative;
  z-index: 30;
  display: grid;
  grid-template-columns: minmax(16rem, 21.25rem) minmax(20rem, 1fr) minmax(
      26rem,
      max-content
    );
  align-items: center;
  gap: 0.75rem;
  width: calc(100% - 2rem);
  min-height: 4.7rem;
  margin: 1.25rem auto 1rem;
  padding: 0.75rem 1.25rem;
  border: 1px solid var(--tic-border);
  border-radius: 1.55rem;
  background:
    linear-gradient(135deg, rgba(255, 255, 255, 0.04), transparent 44%),
    var(--tic-glass);
  box-shadow:
    0 1.35rem 3rem rgba(0, 0, 0, 0.28),
    inset 0 1px 0 rgba(255, 255, 255, 0.08);
  backdrop-filter: none;
  overflow: visible;
}

.tic-topbar:has(.tic-topbar__dropdown),
.tic-topbar:has(.tic-topbar__profile-dropdown) {
  z-index: 11000;
}

.tic-topbar__brand,
.tic-topbar__actions,
.tic-topbar__nav,
.tic-topbar__status,
.tic-topbar__sip,
.tic-topbar__icon-action {
  display: flex;
  align-items: center;
}

.tic-topbar__brand {
  gap: 0.7rem;
  min-width: 0;
}

.tic-topbar__mark {
  display: grid;
  place-items: center;
  flex-shrink: 0;
  width: 2.75rem;
  height: 2.75rem;
  border: 1px solid rgba(38, 217, 247, 0.34);
  border-radius: 50%;
  background: linear-gradient(
    135deg,
    rgba(139, 42, 201, 0.22),
    rgba(38, 217, 247, 0.2)
  );
  box-shadow: 0 0 1.5rem rgba(38, 217, 247, 0.22);
  overflow: hidden;

  img {
    width: 72%;
    height: 72%;
    object-fit: contain;
  }
}

.tic-topbar__brand-text {
  display: flex;
  flex-direction: column;
  min-width: 0;

  strong,
  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  strong {
    color: #fff;
    font-size: 0.92rem;
    line-height: 1.2;
  }

  span {
    color: rgba(226, 232, 240, 0.68);
    font-size: 0.75rem;
  }
}

.tic-topbar__nav {
  justify-content: center;
  gap: 0.78rem;
  min-width: 0;
  width: fit-content;
  max-width: 100%;
  margin-inline: auto;
  overflow-x: auto;
  padding: 0.45rem 0.75rem;
  border: 1px solid rgba(148, 234, 255, 0.08);
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.12);
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.tic-topbar__nav-item,
.tic-topbar__icon-action,
.tic-topbar__status,
.tic-topbar__sip {
  border: 1px solid transparent;
  color: rgba(226, 232, 240, 0.78);
  background: rgba(255, 255, 255, 0.035);
  transition:
    background 0.18s ease,
    border-color 0.18s ease,
    color 0.18s ease,
    transform 0.18s ease;
}

.tic-topbar__nav-item {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.35rem;
  height: 2.35rem;
  padding: 0;
  border-radius: 50%;

  &:hover,
  &.is-active {
    color: #fff;
    border-color: rgba(38, 217, 247, 0.34);
    background:
      linear-gradient(
        135deg,
        rgba(139, 42, 201, 0.48),
        rgba(38, 217, 247, 0.22)
      ),
      rgba(255, 255, 255, 0.07);
  }

  &.is-active {
    width: 2.85rem;
    height: 2.85rem;
    box-shadow:
      0 0 1.6rem rgba(38, 217, 247, 0.35),
      inset 0 1px 0 rgba(255, 255, 255, 0.14);
  }
}

.tic-topbar__button {
  cursor: pointer;
}

.tic-topbar__nav-icon {
  width: 1.12rem;
  height: 1.12rem;
  flex-shrink: 0;
}

.tic-topbar__badge {
  display: grid;
  place-items: center;
  min-width: 1.15rem;
  height: 1.15rem;
  padding: 0 0.25rem;
  border-radius: 999px;
  background: #ff4d8d;
  color: #fff;
  font-size: 0.62rem;
  font-weight: 900;
  position: absolute;
  top: -0.35rem;
  right: -0.3rem;
}

.tic-topbar__actions {
  justify-content: flex-end;
  gap: 0.55rem;
  flex: 0 0 max-content;
  width: max-content;
  min-width: 26rem;
  max-width: none;
  overflow: visible;
  padding: 0.35rem;
  border: 1px solid rgba(148, 234, 255, 0.08);
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.1);
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.tic-topbar__actions > *,
.tic-topbar__actions :deep(.relative.space-y-2) {
  flex: 0 0 auto;
}

.tic-topbar__icon-action,
.tic-topbar__status,
.tic-topbar__sip {
  min-height: 2.35rem;
  border-radius: 999px;
}

.tic-topbar__icon-action {
  justify-content: center;
  gap: 0.35rem;
  width: 2.35rem;
  min-width: 2.35rem;
  padding: 0;

  &:hover,
  &.is-active {
    color: #fff;
    border-color: rgba(139, 42, 201, 0.42);
    background: rgba(139, 42, 201, 0.2);
  }
}

.tic-topbar__search {
  max-width: 2.35rem;
}

.tic-topbar__sip {
  position: relative;
  justify-content: center;
  width: 2.35rem;
  min-width: 2.35rem;
  padding: 0;

  .tic-status-dot {
    position: absolute;
    right: 0.42rem;
    bottom: 0.42rem;
  }
}

.tic-topbar__status {
  gap: 0.45rem;
  justify-content: center;
  flex: 0 0 8.6rem;
  min-width: 8.6rem;
  max-width: 8.6rem;
  padding: 0 0.72rem;
  font-size: 0.76rem;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;

  span:not(.tic-status-dot):not([class*='i-lucide']) {
    line-height: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &:hover,
  &.is-active {
    color: #fff;
    border-color: rgba(38, 217, 247, 0.34);
    background: rgba(38, 217, 247, 0.13);
  }
}

.tic-status-dot {
  width: 0.45rem;
  height: 0.45rem;
  flex-shrink: 0;
  border-radius: 50%;
  background: rgba(148, 163, 184, 0.9);
  box-shadow: 0 0 0.6rem currentColor;
}

.tic-status-dot--online {
  color: #2fffd2;
  background: #2fffd2;
}

.tic-status-dot--busy {
  color: #fbbf24;
  background: #fbbf24;
}

.tic-status-dot--offline {
  color: #94a3b8;
  background: #94a3b8;
}

.tic-topbar__segmented {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.4rem;
  padding: 0.25rem;

  button {
    min-height: 2rem;
    border: 1px solid rgba(148, 234, 255, 0.16);
    border-radius: 0.6rem;
    background: rgba(255, 255, 255, 0.04);
    color: rgba(226, 232, 240, 0.82);
    font-size: 0.75rem;
    font-weight: 700;
    cursor: pointer;

    &:hover,
    &.is-active {
      border-color: rgba(38, 217, 247, 0.35);
      background: rgba(38, 217, 247, 0.16);
      color: #fff;
    }
  }
}

.tic-a11y-panel {
  display: grid;
  gap: 0.45rem;
  padding: 0.25rem;

  button {
    display: flex;
    align-items: center;
    gap: 0.55rem;
    min-height: 2.25rem;
    padding: 0 0.7rem;
    border: 1px solid rgba(148, 234, 255, 0.16);
    border-radius: 0.65rem;
    background: rgba(255, 255, 255, 0.04);
    color: rgba(226, 232, 240, 0.82);
    font-size: 0.78rem;
    font-weight: 750;
    text-align: left;
    cursor: pointer;

    &:hover,
    &.is-active {
      border-color: rgba(38, 217, 247, 0.36);
      background: rgba(38, 217, 247, 0.14);
      color: #fff;
    }
  }
}

:global(body:not(.dark)) .tic-topbar {
  --tic-glass: #ffffff;
  --tic-border: rgba(255, 255, 255, 0.9);

  color: #0f172a;
  background: #ffffff;
  box-shadow:
    0 12px 32px 0 rgba(31, 38, 135, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.82);
}

:global(body:not(.dark)) .tic-topbar__brand-text strong,
:global(body:not(.dark)) .tic-topbar__nav-item:hover,
:global(body:not(.dark)) .tic-topbar__nav-item.is-active,
:global(body:not(.dark)) .tic-topbar__icon-action:hover,
:global(body:not(.dark)) .tic-topbar__icon-action.is-active {
  color: #08111f;
}

:global(body:not(.dark)) .tic-topbar__brand-text span,
:global(body:not(.dark)) .tic-topbar__nav-item,
:global(body:not(.dark)) .tic-topbar__icon-action,
:global(body:not(.dark)) .tic-topbar__status,
:global(body:not(.dark)) .tic-topbar__sip {
  color: rgba(15, 23, 42, 0.72);
}

:global(body:not(.dark)) .tic-topbar__nav,
:global(body:not(.dark)) .tic-topbar__actions {
  background: transparent;
}

:global(body:not(.dark)) .tic-topbar__nav-item,
:global(body:not(.dark)) .tic-topbar__icon-action,
:global(body:not(.dark)) .tic-topbar__status,
:global(body:not(.dark)) .tic-topbar__sip {
  background: rgba(255, 255, 255, 0.5);
  border-color: rgba(0, 0, 0, 0.08);
}

:global(body:not(.dark)) .tic-topbar__nav-item:hover,
:global(body:not(.dark)) .tic-topbar__icon-action:hover,
:global(body:not(.dark)) .tic-topbar__status:hover,
:global(body:not(.dark)) .tic-topbar__sip:hover {
  background: rgba(0, 0, 0, 0.05);
  color: #0f172a;
}

:global(body:not(.dark)) .tic-topbar__nav-item.is-active,
:global(body:not(.dark)) .tic-topbar__icon-action.is-active {
  color: #fff;
  background: linear-gradient(135deg, #8b2ac9, #26d9f7);
  box-shadow: 0 4px 15px rgba(38, 217, 247, 0.3);
}

:global(body:not(.dark)) .tic-topbar__dropdown .tic-topbar__segmented button,
:global(body:not(.dark)) .tic-topbar__dropdown .tic-a11y-panel button {
  background: #ffffff;
  border-color: rgba(15, 23, 42, 0.08);
  color: #1f2937;
  opacity: 1;
}

:global(body:not(.dark)) .tic-topbar__dropdown {
  background: #ffffff !important;
  color: #0f172a !important;
  opacity: 1 !important;
}

:global(body:not(.dark)) .tic-topbar__dropdown * {
  opacity: 1;
}

:global(body:not(.dark)) .tic-topbar__dropdown :deep(.n-dropdown-section-title),
:global(body:not(.dark)) .tic-topbar__dropdown :deep(.dropdown-section-title) {
  color: #334155 !important;
}

:global(body:not(.dark))
  .tic-topbar__dropdown
  .tic-topbar__segmented
  button:hover,
:global(body:not(.dark))
  .tic-topbar__dropdown
  .tic-topbar__segmented
  button.is-active,
:global(body:not(.dark)) .tic-topbar__dropdown .tic-a11y-panel button:hover,
:global(body:not(.dark))
  .tic-topbar__dropdown
  .tic-a11y-panel
  button.is-active {
  background: rgba(38, 217, 247, 0.14);
  border-color: rgba(38, 217, 247, 0.36);
  color: #0f172a;
}

.tic-topbar__profile {
  flex: 0 0 3rem;
  width: 3rem;
  max-width: 3rem;
}

.tic-topbar__dropdown,
:global(.tic-topbar__profile-dropdown) {
  position: fixed !important;
  top: 5.85rem !important;
  right: 2rem !important;
  left: auto !important;
  z-index: 12000 !important;
}

@media (max-width: 1180px) {
  .tic-topbar {
    grid-template-columns: minmax(0, 1fr) max-content;
    gap: 0.8rem;
  }

  .tic-topbar__brand {
    grid-column: 1;
    grid-row: 1;
  }

  .tic-topbar__actions {
    grid-column: 2;
    grid-row: 1;
  }

  .tic-topbar__actions {
    width: max-content;
    min-width: 0;
    max-width: 100%;
  }

  .tic-topbar__nav {
    grid-column: 1 / -1;
    grid-row: 2;
    justify-content: flex-start;
    width: 100%;
  }

  .tic-topbar__dropdown,
  :global(.tic-topbar__profile-dropdown) {
    top: 9.75rem !important;
  }
}

@media (max-width: 720px) {
  .tic-topbar {
    width: calc(100% - 1rem);
    margin-top: 0.75rem;
    margin-bottom: 0.75rem;
  }

  .tic-topbar__brand-text,
  .tic-topbar__status span:not(.tic-status-dot) {
    display: none;
  }

  .tic-topbar__status {
    flex-basis: 2.35rem;
    width: 2.35rem;
    min-width: 2.35rem;
    max-width: 2.35rem;
    padding: 0;
  }

  .tic-topbar__actions {
    gap: 0.35rem;
  }

  .tic-topbar__profile {
    width: 3rem;
    max-width: 3rem;
  }

  .tic-topbar__dropdown,
  :global(.tic-topbar__profile-dropdown) {
    top: 9.25rem !important;
    right: 0.75rem !important;
    width: min(20rem, calc(100vw - 1.5rem)) !important;
  }
}

@media (max-width: 520px) {
  .tic-topbar {
    grid-template-columns: 1fr;
    padding: 0.75rem;
  }

  .tic-topbar__brand {
    display: none;
  }

  .tic-topbar__actions {
    grid-column: 1;
    justify-content: space-between;
    width: 100%;
  }

  .tic-topbar__nav {
    grid-row: 2;
  }
}
</style>
